"use client"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import API from "../services/api"
import "./OffresPage.css"
import CompressedFileInput from "../components/CompressedFileInput"
import ModalConfigEditor from "../components/ModalConfigEditor"
import ConfirmDialog from "../components/ConfirmDialog"
import Toast, { useToast } from "../components/Toast"

const SERVER_BASE = "https://api.ibistunis.com"

const getMediaUrl = (url) => {
  if (!url) return null
  if (url.startsWith("blob:") || url.startsWith("http")) return url
  return `${SERVER_BASE}${url.startsWith("/") ? "" : "/"}${url}`
}

const OffresPage = () => {
  const navigate = useNavigate()
  const [offres, setOffres] = useState([])
  const [toasts, setToasts] = useState([])
  const { addToast, removeToast } = useToast(setToasts)
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: "", onConfirm: null })
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    discountPercentage: "",
    startDate: "",
    endDate: "",
    active: true,
    image: null,
    video: null,
    videoUrl: "",
    translations: {
      fr: { title: "", description: "" },
      en: { title: "", description: "" },
      ar: { title: "", description: "" },
    },
  })

  const [previewImage, setPreviewImage] = useState(null)
  const [editId, setEditId] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [user, setUser] = useState({ username: "", email: "" })
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [searchTerm, setSearchTerm] = useState("")
  const [sortOrder, setSortOrder] = useState("newest")
  const [viewMode, setViewMode] = useState("grid")
  const [showModalConfigEditor, setShowModalConfigEditor] = useState(false)
  const [currentLang, setCurrentLang] = useState("fr")
  const [activeVideo, setActiveVideo] = useState(null) // State for video modal

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("theme")
    return savedTheme ? savedTheme === "dark" : true
  })

  const fetchOffres = async () => {
    try {
      setIsLoading(true)
      const res = await API.get("/offres")
      setOffres(res.data)
    } catch (error) {
      console.error("Erreur chargement offres:", error)
      addToast("Erreur de chargement", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      message: "Êtes-vous sûr de vouloir supprimer cette offre ?",
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: "", onConfirm: null })
        try {
          setIsLoading(true)
          await API.delete(`/offres/${id}`)
          addToast("Offre supprimée avec succès", "success")
          fetchOffres()
        } catch (error) {
          console.error("Erreur suppression offre:", error)
          addToast("Erreur lors de la suppression", "error")
        } finally {
          setIsLoading(false)
        }
      },
    })
  }

  useEffect(() => {
    const token = localStorage.getItem("accessToken")
    if (!token) navigate("/")

    const userData = JSON.parse(localStorage.getItem("user") || '{"username":"test","email":"test@gmail.com"}')
    setUser(userData)

    const loadTimer = setTimeout(() => setIsLoading(false), 800)
    const timer = setInterval(() => setCurrentTime(new Date()), 60000)

    fetchOffres()

    return () => {
      clearTimeout(loadTimer)
      clearInterval(timer)
    }
  }, [navigate])

  useEffect(() => {
    localStorage.setItem("theme", isDarkMode ? "dark" : "light")
  }, [isDarkMode])

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
  }

  const handleChange = (e) => {
    const { name, value, files } = e.target

    if (name === "image" && files && files[0]) {
      const image = files[0]
      setFormData((prev) => ({ ...prev, image }))

      const reader = new FileReader()
      reader.onloadend = () => setPreviewImage(reader.result)
      reader.readAsDataURL(image)
      return
    }

    if (name === "video" && files && files[0]) {
      const video = files[0]
      const previewUrl = URL.createObjectURL(video)
      setFormData((prev) => ({ ...prev, video, videoUrl: previewUrl }))
      return
    }

    if (name === "title" || name === "description") {
      setFormData((prev) => {
        const safeTranslations = prev.translations || {
          fr: { title: "", description: "" },
          en: { title: "", description: "" },
          ar: { title: "", description: "" },
        }

        const updatedTranslations = {
          ...safeTranslations,
          [currentLang]: {
            ...(safeTranslations[currentLang] || { title: "", description: "" }),
            [name]: value,
          },
        }

        return {
          ...prev,
          [name]: currentLang === "fr" ? value : prev[name],
          translations: updatedTranslations,
        }
      })
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleRemoveMedia = (type) => {
    if (type === "image") {
      setFormData((prev) => ({ ...prev, image: null }))
      setPreviewImage(null)
    } else if (type === "video") {
      setFormData((prev) => ({ ...prev, video: null, videoUrl: "" }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const form = new FormData()

      const translationsData = formData.translations || {
        fr: { title: formData.title || "", description: formData.description || "" },
        en: { title: "", description: "" },
        ar: { title: "", description: "" },
      }

      form.append("title", translationsData.fr?.title || formData.title || "")
      form.append("description", translationsData.fr?.description || formData.description || "")
      form.append("discountPercentage", formData.discountPercentage)
      form.append("startDate", formData.startDate)
      form.append("endDate", formData.endDate)
      form.append("active", formData.active)
      form.append("translations", JSON.stringify(translationsData || {}))

      if (formData.image) {
        form.append("image", formData.image)
      }

      if (formData.video) {
        form.append("video", formData.video)
      }

      if (editId) {
        await API.put(`/offres/${editId}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        addToast("Offre modifiée avec succès", "success")
      } else {
        await API.post("/offres", form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        addToast("Offre créée avec succès", "success")
      }

      fetchOffres()
      resetForm()
    } catch (error) {
      console.error("Erreur enregistrement:", error)
      addToast("Erreur lors de l'enregistrement", "error")
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (offre) => {
    const existingTranslations = offre.translations || {
      fr: { title: offre.title || "", description: offre.description || "" },
      en: { title: "", description: "" },
      ar: { title: "", description: "" },
    }

    setFormData({
      title: existingTranslations.fr?.title || offre.title || "",
      description: existingTranslations.fr?.description || offre.description || "",
      discountPercentage: offre.discountPercentage,
      startDate: new Date(offre.startDate).toISOString().slice(0, 16),
      endDate: new Date(offre.endDate).toISOString().slice(0, 16),
      active: offre.active,
      image: null,
      video: null,
      videoUrl: (typeof offre.video === "string" ? offre.video : offre.video?.url) || 
                (typeof offre.videoUrl === "string" ? offre.videoUrl : offre.videoUrl?.url) || "",
      translations: {
        fr: {
          title: existingTranslations.fr?.title || offre.title || "",
          description: existingTranslations.fr?.description || offre.description || "",
        },
        en: {
          title: existingTranslations.en?.title || "",
          description: existingTranslations.en?.description || "",
        },
        ar: {
          title: existingTranslations.ar?.title || "",
          description: existingTranslations.ar?.description || "",
        },
      },
    })
    setPreviewImage(offre.image)
    setEditId(offre._id)
    setShowForm(true)
    setCurrentLang("fr")
  }

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      discountPercentage: "",
      startDate: "",
      endDate: "",
      active: true,
      image: null,
      video: null,
      videoUrl: "",
      translations: {
        fr: { title: "", description: "" },
        en: { title: "", description: "" },
        ar: { title: "", description: "" },
      },
    })
    setPreviewImage(null)
    setEditId(null)
    setShowForm(false)
  }

  const formatDate = (date) => {
    const options = { weekday: "long", day: "numeric", month: "long", year: "numeric" }
    return date.toLocaleDateString("fr-FR", options)
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  }

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const filteredOffres = offres
    .filter(
      (o) =>
        o.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.description?.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => {
      if (sortOrder === "newest") {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      } else if (sortOrder === "oldest") {
        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0)
      } else if (sortOrder === "alphabetical") {
        return a.title.localeCompare(b.title)
      } else if (sortOrder === "discount-high") {
        return b.discount - a.discount
      } else if (sortOrder === "discount-low") {
        return a.discount - b.discount
      }
      return 0
    })

  if (isLoading) {
    return (
      <div className={isDarkMode ? "loading-container" : "light-loading-container"}>
        <div className={isDarkMode ? "loading-logo" : "light-loading-logo"}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"} 
    alt="Guestly Logo"
    className="logo-image"
  />
</div>
        <div className={isDarkMode ? "loading-spinner" : "light-loading-spinner"}>
          <div className={isDarkMode ? "spinner-circle" : "light-spinner-circle"}></div>
          <div className={isDarkMode ? "spinner-circle-inner" : "light-spinner-circle-inner"}></div>
        </div>
        <div className={isDarkMode ? "loading-text" : "light-loading-text"}>Chargement...</div>
      </div>
    )
  }

  return (
    <div className={isDarkMode ? "dashboard" : "light-dashboard"}>
      <div className={isDarkMode ? "mobile-header" : "light-mobile-header"}>
        <button className={isDarkMode ? "menu-toggle" : "light-menu-toggle"} onClick={toggleSidebar}>
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className={isDarkMode ? "mobile-logo" : "light-mobile-logo"}>
          <span style={{ display: "inline-block", verticalAlign: "middle" }}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
    alt="Guestly Logo"
    style={{
      width: "180px",        // wide logo
      height: "auto",        // maintain aspect ratio
      objectFit: "contain",
      transition: "opacity 0.3s ease"
    }}
  />
</span>

        </div>
        <div className={isDarkMode ? "mobile-user" : "light-mobile-user"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
        </div>
      </div>

      <div className={`${isDarkMode ? "sidebar" : "light-sidebar"} ${isSidebarOpen ? "open" : ""}`}>
        <div className={isDarkMode ? "sidebar-header" : "light-sidebar-header"}>
          <div className={isDarkMode ? "logo" : "light-logo"}>
            <span style={{ display: "inline-block", verticalAlign: "middle" }}>
  <img
    src={isDarkMode ? "/GUESTLY_LIGHT.jpg" : "/GUESTLY_DARK.jpg"}
    alt="Guestly Logo"
    style={{
      width: "180px",        // wide logo
      height: "auto",        // maintain aspect ratio
      objectFit: "contain",
      transition: "opacity 0.3s ease"
    }}
  />
</span>

          </div>
          <button className={isDarkMode ? "close-sidebar" : "light-close-sidebar"} onClick={toggleSidebar}>
            ×
          </button>
        </div>

        <div className={isDarkMode ? "user-profile" : "light-user-profile"}>
          <div className={isDarkMode ? "user-avatar" : "light-user-avatar"}>{user.username.charAt(0)}</div>
          <div className={isDarkMode ? "user-info" : "light-user-info"}>
            <div className={isDarkMode ? "user-name" : "light-user-name"}>{user.username}</div>
            <div className={isDarkMode ? "user-email" : "light-user-email"}>{user.email}</div>
          </div>
        </div>

        <div className={isDarkMode ? "sidebar-date" : "light-sidebar-date"}>
          <div className={isDarkMode ? "date-display" : "light-date-display"}>{formatDate(currentTime)}</div>
          <div className={isDarkMode ? "time-display" : "light-time-display"}>{formatTime(currentTime)}</div>
        </div>

        <nav className={isDarkMode ? "sidebar-nav" : "light-sidebar-nav"}>
          <ul>
            <li className="active">
              <a href="/dashboard">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🏠</span>
                <span>Tableau de bord</span>
              </a>
            </li>
            <li>
              <a href="/profile">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>👤</span>
                <span>Mon profil</span>
              </a>
            </li>
            <li>
              <a href="/statistiques">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>📊</span>
                <span>Statistiques</span>
              </a>
            </li>
            <li>
              <a href="/historique">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>📋</span>
                <span>Historique</span>
              </a>
            </li>
            <li>
              <a href="/settings">
                <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>⚙️</span>
                <span>Paramètres</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className={isDarkMode ? "sidebar-footer" : "light-sidebar-footer"}>
          <button
            className={isDarkMode ? "logout-button" : "light-logout-button"}
            onClick={() => {
              localStorage.removeItem("accessToken")
              localStorage.removeItem("user")
              navigate("/")
            }}
          >
            <span className={isDarkMode ? "nav-icon" : "light-nav-icon"}>🚪</span>
            <span>Déconnexion</span>
          </button>
        </div>
      </div>

      <div className={isDarkMode ? "main-content" : "light-main-content"}>
        <div className={isDarkMode ? "welcome-section" : "light-welcome-section"}>
          <div className={isDarkMode ? "welcome-header" : "light-welcome-header"}>
            <h1>
              Offres Spéciales <span className={isDarkMode ? "wave-emoji" : "light-wave-emoji"}>🎯</span>
            </h1>
            <div className={isDarkMode ? "welcome-actions" : "light-welcome-actions"}>
              <button
                className={isDarkMode ? "action-button" : "light-action-button"}
                onClick={() => setShowModalConfigEditor(true)}
                title="Modifier l'affichage de la modal"
              >
                <span className={isDarkMode ? "action-icon" : "light-action-icon"}>⚙️</span>
              </button>

              <button
                className={isDarkMode ? "theme-toggle-button" : "light-theme-toggle-button"}
                onClick={toggleTheme}
                title={isDarkMode ? "Passer en mode clair" : "Passer en mode sombre"}
              >
                {isDarkMode ? "☀️" : "🌙"}
              </button>

              <div className={isDarkMode ? "user-avatar-small" : "light-user-avatar-small"}>
                {user.username.charAt(0)}
              </div>
            </div>
          </div>
          <p>Gérez vos offres et promotions spéciales</p>
        </div>

        <div className={isDarkMode ? "search-filter-container" : "light-search-filter-container"}>
          <div className={isDarkMode ? "search-container" : "light-search-container"}>
            <span className="search-icon">🔍</span>
            <input
              className={isDarkMode ? "search-input" : "light-search-input"}
              type="text"
              placeholder="Rechercher une offre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="view-sort-container">
            <div className="view-toggle">
              <button
                className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-label="Vue en grille"
              >
                ▦
              </button>
              <button
                className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-label="Vue en liste"
              >
                ☰
              </button>
            </div>

            <select
              className={isDarkMode ? "sort-select" : "light-sort-select"}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="newest">Plus récent</option>
              <option value="oldest">Plus ancien</option>
              <option value="alphabetical">Alphabétique</option>
              <option value="discount-high">Remise (décroissant)</option>
              <option value="discount-low">Remise (croissant)</option>
            </select>
          </div>
        </div>

        <div className={isDarkMode ? "section-header" : "light-section-header"}>
          <h2>Liste des offres</h2>
          <div className="section-actions">
            <button
              className={isDarkMode ? "section-action" : "light-section-action"}
              onClick={() => setShowForm(!showForm)}
            >
              <span>{showForm ? "Annuler" : "Ajouter"}</span>
              <span className="action-icon">{showForm ? "❌" : "+"}</span>
            </button>
          </div>
        </div>

        {showForm && (
          <div className="op-form-container">
            <div className="op-form-header">
              <h3>{editId ? "✏️ Modifier l'offre" : "✨ Nouvelle offre"}</h3>
              <div className="language-toggle">
                {["fr", "ar", "en"].map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    className={`lang-btn ${currentLang === lang ? "active" : ""} ${isDarkMode ? "" : "light"}`}
                    onClick={() => setCurrentLang(lang)}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <form onSubmit={handleSubmit} className="op-form">
              {/* Row 1: title + discount */}
              <div className="op-form-row">
                <div className="op-form-group op-form-group-2">
                  <label className="op-label">Titre *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.translations?.[currentLang]?.title || ""}
                    onChange={handleChange}
                    placeholder="Titre de l'offre"
                    required
                    className="op-input"
                  />
                </div>
                <div className="op-form-group">
                  <label className="op-label">Remise (%) *</label>
                  <input
                    type="number"
                    name="discountPercentage"
                    value={formData.discountPercentage}
                    onChange={handleChange}
                    placeholder="ex: 20"
                    min="0"
                    max="100"
                    required
                    className="op-input"
                  />
                </div>
              </div>

              {/* Row 2: description */}
              <div className="op-form-group">
                <label className="op-label">Description</label>
                <textarea
                  name="description"
                  value={formData.translations?.[currentLang]?.description || ""}
                  onChange={handleChange}
                  placeholder="Décrivez cette offre..."
                  rows={3}
                  className="op-input op-textarea"
                />
              </div>

              {/* Row 3: dates */}
              <div className="op-form-row">
                <div className="op-form-group">
                  <label className="op-label">📅 Date de début *</label>
                  <input
                    type="datetime-local"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    className="op-input"
                  />
                </div>
                <div className="op-form-group">
                  <label className="op-label">📅 Date de fin *</label>
                  <input
                    type="datetime-local"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    className="op-input"
                  />
                </div>
              </div>

              {/* Row 4: media */}
              <div className="op-form-row">
                <div className="op-form-group">
                  <label className="op-label">🖼️ Image</label>
                  <div className="op-upload-zone">
                    {previewImage ? (
                      <div className="op-preview-wrap">
                        <img src={getMediaUrl(previewImage)} alt="Aperçu" className="op-preview-img" />
                        <div className="op-preview-overlay">
                          <span className="op-preview-change">Changer</span>
                          <button 
                            type="button" 
                            className="op-media-remove-btn"
                            onClick={(e) => { e.stopPropagation(); handleRemoveMedia("image"); }}
                            title="Supprimer l'image"
                          >
                            ×
                          </button>
                        </div>
                        <CompressedFileInput
                          type="file"
                          name="image"
                          accept="image/*"
                          onChange={handleChange}
                          className="op-upload-hidden"
                        />
                      </div>
                    ) : (
                      <div className="op-upload-placeholder">
                        <span className="op-upload-icon">📷</span>
                        <span>Cliquez pour ajouter une image</span>
                        <CompressedFileInput
                          type="file"
                          name="image"
                          accept="image/*"
                          onChange={handleChange}
                          className="op-upload-hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div className="op-form-group">
                  <label className="op-label">🎬 Vidéo (optionnel)</label>
                  <div className="op-upload-zone">
                    {formData.videoUrl ? (
                      <div className="op-video-preview-wrap">
                        <video
                          src={getMediaUrl(formData.videoUrl)}
                          className="op-preview-video"
                          controls
                          muted
                          playsInline
                        />
                        <div className="op-video-preview-actions">
                          <label className="op-video-change-btn">
                            🔄 Changer
                            <input
                              type="file"
                              name="video"
                              accept="video/*"
                              onChange={handleChange}
                              style={{ display: "none" }}
                            />
                          </label>
                          <button 
                            type="button" 
                            className="op-media-remove-btn op-media-remove-btn-video"
                            onClick={(e) => { e.stopPropagation(); handleRemoveMedia("video"); }}
                            title="Supprimer la vidéo"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="op-upload-placeholder">
                        <span className="op-upload-icon">🎬</span>
                        <span>Cliquez pour ajouter une vidéo</span>
                        <input
                          type="file"
                          name="video"
                          accept="video/*"
                          onChange={handleChange}
                          className="op-upload-hidden"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Row 5: active toggle + actions */}
              <div className="op-form-footer">
                <label className="op-toggle-label">
                  <div className={`op-toggle ${formData.active ? "op-toggle-on" : ""}`}
                    onClick={() => setFormData(prev => ({ ...prev, active: !prev.active }))}>
                    <div className="op-toggle-knob" />
                  </div>
                  <span>Offre {formData.active ? "active" : "inactive"}</span>
                </label>
                <div className="op-form-actions">
                  <button type="button" className="op-btn-cancel" onClick={resetForm}>
                    Annuler
                  </button>
                  <button type="submit" className="op-btn-primary" disabled={isLoading}>
                    {isLoading ? "Enregistrement..." : editId ? "✅ Enregistrer" : "✨ Créer l'offre"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Stats bar */}
        <div className="op-stats-bar">
          <div className="op-stat">
            <span className="op-stat-num">{offres.length}</span>
            <span className="op-stat-label">Total</span>
          </div>
          <div className="op-stat op-stat-active">
            <span className="op-stat-num">{offres.filter(o => o.active).length}</span>
            <span className="op-stat-label">Actives</span>
          </div>
          <div className="op-stat op-stat-inactive">
            <span className="op-stat-num">{offres.filter(o => !o.active).length}</span>
            <span className="op-stat-label">Inactives</span>
          </div>
          <div className="op-stat op-stat-video">
            <span className="op-stat-num">{offres.filter(o => o.video || o.videoUrl).length}</span>
            <span className="op-stat-label">Avec vidéo</span>
          </div>
        </div>

        {filteredOffres.length === 0 ? (
          <div className="op-empty-state">
            <div className="op-empty-icon">🎯</div>
            <h3>Aucune offre trouvée</h3>
            <p>{searchTerm ? "Essayez un autre terme de recherche" : "Commencez par créer une nouvelle offre"}</p>
            {!searchTerm && (
              <button className="op-btn-primary" onClick={() => setShowForm(true)}>
                + Créer une offre
              </button>
            )}
          </div>
        ) : (
          <div className={`op-cards-grid ${viewMode === "list" ? "op-list-view" : ""}`}>
            {filteredOffres.map((offre) => {
              const isExpired = offre.endDate && new Date(offre.endDate) < new Date()
              return (
                <div key={offre._id} className={`op-card ${!offre.active || isExpired ? "op-card-inactive" : ""} ${viewMode === "list" ? "op-card-list" : ""}`}>
                  {/* Media area */}
                  <div 
                    className="op-card-media"
                    onClick={() => {
                      if (offre.video || offre.videoUrl) {
                        setActiveVideo(getMediaUrl(offre.video || offre.videoUrl))
                      }
                    }}
                  >
                    {offre.image ? (
                      <img src={getMediaUrl(offre.image)} alt={offre.title} className="op-card-media-el" />
                    ) : (
                      <div className="op-card-media-placeholder">🎯</div>
                    )}
                    
                    {/* Video Play Overlay */}
                    {(offre.video || offre.videoUrl) && (
                      <div className="op-video-overlay-icon">
                        <span className="op-play-button">▶</span>
                      </div>
                    )}

                    {/* Discount badge */}
                    <div className="op-discount-badge">−{offre.discountPercentage}%</div>
                    {/* Status pill */}
                    <div className={`op-status-pill ${offre.active && !isExpired ? "op-pill-active" : "op-pill-inactive"}`}>
                      {isExpired ? "Expirée" : offre.active ? "Active" : "Inactive"}
                    </div>
                  </div>

                  {/* Card body */}
                  <div className="op-card-body">
                    <h3 className="op-card-title">{offre.title}</h3>
                    {offre.description && (
                      <p className="op-card-desc">{offre.description}</p>
                    )}
                    <div className="op-card-meta">
                      <div className="op-meta-row">
                        <span className="op-meta-icon">📅</span>
                        <span>{new Date(offre.startDate).toLocaleDateString("fr-FR")} → {new Date(offre.endDate).toLocaleDateString("fr-FR")}</span>
                      </div>
                    </div>
                    <div className="op-card-actions">
                      <button className="op-btn-edit" onClick={() => handleEdit(offre)}>
                        ✏️ Modifier
                      </button>
                      <button className="op-btn-delete" onClick={() => handleDelete(offre._id)}>
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {showModalConfigEditor && (
        <ModalConfigEditor isDarkMode={isDarkMode} onClose={() => setShowModalConfigEditor(false)} />
      )}

      {/* Video Lightbox Modal */}
      {activeVideo && (
        <div className="op-video-lightbox" onClick={() => setActiveVideo(null)}>
          <div className="op-video-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="op-video-close" onClick={() => setActiveVideo(null)}>×</button>
            <video src={activeVideo} controls autoPlay className="op-lightbox-video" />
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        message={confirmDialog.message}
        isDarkMode={isDarkMode}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, message: "", onConfirm: null })}
      />
      <Toast toasts={toasts} removeToast={removeToast} />

      <style jsx>{`
/* ─── Base reset helpers ─────────────────────────────── */
button, input, select, textarea, a {
  pointer-events: auto !important;
  position: relative;
  z-index: 1;
}
* { box-sizing: border-box; }

/* ─── Language toggle ────────────────────────────────── */
.language-toggle {
  display: inline-flex;
  gap: 0.4rem;
}
.lang-btn {
  padding: 0.35rem 0.85rem;
  border-radius: 999px;
  border: 2px solid #cbd5e1;
  background: #f1f5f9;
  color: #334155;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  letter-spacing: 0.03em;
}
.lang-btn.light {
  border-color: #cbd5e1;
  color: #334155;
  background: #f1f5f9;
}
.lang-btn.active {
  background: linear-gradient(45deg, #ec4899, #8b5cf6);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 8px rgba(236,72,153,0.4);
}
.lang-btn:not(.active):hover {
  border-color: #ec4899;
  color: #ec4899;
  background: rgba(236,72,153,0.06);
}
.lang-btn.light:not(.active):hover {
  border-color: #ec4899;
  color: #ec4899;
  background: rgba(236,72,153,0.06);
}

/* ─── Dashboard shell ────────────────────────────────── */
.light-dashboard {
  background: #f8fafc;
  color: #1e293b;
  min-height: 100vh;
  font-family: "Inter", "Segoe UI", sans-serif;
}
.light-dashboard input[type="text"],
.light-dashboard input[type="number"],
.light-dashboard input[type="email"],
.light-dashboard input[type="file"],
.light-dashboard input[type="datetime-local"],
.light-dashboard input[type="url"],
.light-dashboard textarea { cursor: text !important; }
.light-dashboard input[type="checkbox"] { cursor: pointer !important; }

/* ─── Loading ────────────────────────────────────────── */
.light-loading-container {
  position: fixed; inset: 0;
  background: linear-gradient(135deg,#fdf2f8,#f3e8ff,#fff);
  display: flex; flex-direction: column;
  justify-content: center; align-items: center;
  z-index: 9999;
}
.light-loading-logo { font-size:3rem; font-weight:700;
  background:linear-gradient(45deg,#ec4899,#8b5cf6);
  -webkit-background-clip:text; -webkit-text-fill-color:transparent;
  background-clip:text; margin-bottom:2rem;
  animation:lightPulse 2s ease-in-out infinite; }
.light-loading-spinner { position:relative; width:80px; height:80px; margin-bottom:2rem; }
.light-spinner-circle { position:absolute; inset:0;
  border:4px solid #f1f5f9; border-top-color:#ec4899;
  border-radius:50%; animation:lightSpin 1s linear infinite; }
.light-spinner-circle-inner { position:absolute; inset:10px;
  border:2px solid transparent; border-top-color:#8b5cf6;
  border-radius:50%; animation:lightSpin 0.8s linear infinite reverse; }
.light-loading-text { font-size:1.1rem; color:#64748b; font-weight:500; }
@keyframes lightPulse { 0%,100%{opacity:1} 50%{opacity:0.7} }
@keyframes lightSpin { to{transform:rotate(360deg)} }

/* ─── Mobile header ─────────────────────────────────── */
.light-mobile-header {
  display:none; background:#fff; border-bottom:1px solid #e2e8f0;
  padding:1rem; justify-content:space-between; align-items:center;
  box-shadow:0 2px 8px rgba(0,0,0,0.06); position:sticky; top:0; z-index:200;
}
.light-menu-toggle { background:none; border:none; cursor:pointer;
  display:flex; flex-direction:column; gap:4px; padding:8px;
  border-radius:8px; transition:all 0.3s ease; }
.light-menu-toggle:hover { background:rgba(236,72,153,0.08); }
.light-menu-toggle span { width:24px; height:2.5px;
  background:linear-gradient(45deg,#ec4899,#8b5cf6);
  border-radius:2px; transition:all 0.3s; }
.light-mobile-user .light-user-avatar {
  width:38px; height:38px; border-radius:50%;
  background:linear-gradient(45deg,#ec4899,#8b5cf6);
  display:flex; align-items:center; justify-content:center;
  color:#fff; font-weight:700; font-size:1rem; }

/* ─── Sidebar ────────────────────────────────────────── */
.light-sidebar {
  width:260px; background:#fff; border-right:1px solid #f1f5f9;
  height:100vh; position:fixed; left:0; top:0; z-index:1000;
  display:flex; flex-direction:column;
  box-shadow:2px 0 16px rgba(0,0,0,0.06); transition:transform 0.3s ease;
}
.light-sidebar-header {
  padding:1.5rem 1.25rem 1rem; border-bottom:1px solid #f1f5f9;
  display:flex; justify-content:space-between; align-items:center;
}
.light-close-sidebar { display:none; background:none; border:none;
  font-size:1.4rem; cursor:pointer; color:#94a3b8; padding:6px;
  border-radius:8px; transition:all 0.2s; }
.light-close-sidebar:hover { background:rgba(236,72,153,0.08); color:#ec4899; }
.light-user-profile { padding:1.25rem; display:flex; align-items:center;
  gap:0.875rem; border-bottom:1px solid #f1f5f9; }
.light-user-avatar { width:44px; height:44px; border-radius:50%;
  background:linear-gradient(45deg,#ec4899,#8b5cf6);
  display:flex; align-items:center; justify-content:center;
  color:#fff; font-weight:700; font-size:1.1rem;
  box-shadow:0 4px 10px rgba(236,72,153,0.25); flex-shrink:0; }
.light-user-name { font-weight:600; color:#1e293b; font-size:0.9rem; }
.light-user-email { font-size:0.78rem; color:#94a3b8; }
.light-sidebar-date { padding:0.875rem 1.25rem; border-bottom:1px solid #f1f5f9;
  text-align:center; }
.light-date-display { font-size:0.78rem; color:#94a3b8; margin-bottom:2px; text-transform:capitalize; }
.light-time-display { font-size:1.4rem; font-weight:700;
  background:linear-gradient(45deg,#ec4899,#8b5cf6);
  -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
.light-sidebar-nav { flex:1; padding:0.75rem 0; overflow-y:auto; }
.light-sidebar-nav ul { list-style:none; padding:0; margin:0; }
.light-sidebar-nav li { margin:2px 0; }
.light-sidebar-nav a {
  display:flex; align-items:center; gap:0.75rem;
  padding:0.65rem 1.25rem; color:#64748b; text-decoration:none;
  transition:all 0.25s; border-radius:0 20px 20px 0;
  margin-right:0.875rem; font-size:0.9rem; position:relative;
}
.light-sidebar-nav a::before {
  content:""; position:absolute; left:0; top:0; bottom:0; width:3px;
  background:linear-gradient(to bottom,#ec4899,#8b5cf6);
  border-radius:0 3px 3px 0; transform:scaleY(0); transition:transform 0.25s;
}
.light-sidebar-nav a:hover { background:rgba(236,72,153,0.06); color:#ec4899; transform:translateX(4px); }
.light-sidebar-nav a:hover::before { transform:scaleY(1); }
.light-sidebar-nav li.active a { background:rgba(236,72,153,0.08); color:#ec4899; font-weight:600; }
.light-sidebar-nav li.active a::before { transform:scaleY(1); }
.light-nav-icon { font-size:1.1rem; }
.light-sidebar-footer { padding:1rem 1.25rem; border-top:1px solid #f1f5f9; }
.light-logout-button {
  width:100%; display:flex; align-items:center; gap:0.75rem;
  padding:0.7rem 1rem; background:linear-gradient(45deg,#ec4899,#8b5cf6);
  color:#fff; border:none; border-radius:12px; cursor:pointer;
  transition:all 0.3s; font-weight:500; font-size:0.9rem;
  box-shadow:0 4px 10px rgba(236,72,153,0.25); }
.light-logout-button:hover { transform:translateY(-2px); box-shadow:0 8px 18px rgba(236,72,153,0.35); }

/* ─── Main content ───────────────────────────────────── */
.light-main-content {
  margin-left:260px; padding:2rem 2.5rem;
  min-height:100vh; background:#f8fafc;
}
.light-welcome-section { margin-bottom:1.75rem; }
.light-welcome-header {
  display:flex; justify-content:space-between; align-items:center; margin-bottom:0.375rem;
}
.light-welcome-header h1 { font-size:2.25rem; font-weight:800; color:#0f172a; margin:0; letter-spacing:-0.02em; }
.light-wave-emoji { display:inline-block; animation:lightWave 2s ease-in-out infinite; }
@keyframes lightWave { 0%,100%{transform:rotate(0)} 25%{transform:rotate(20deg)} 75%{transform:rotate(-10deg)} }
.light-welcome-section>p { color:#64748b; font-size:1rem; margin:0; }
.light-welcome-actions { display:flex; align-items:center; gap:0.75rem; }
.light-action-button,
.light-theme-toggle-button {
  background:#fff; border:1.5px solid #e2e8f0; border-radius:50%;
  width:44px; height:44px; display:flex; align-items:center; justify-content:center;
  cursor:pointer; transition:all 0.25s; box-shadow:0 2px 8px rgba(0,0,0,0.06);
  font-size:1.1rem; z-index:1;
}
.light-action-button:hover,
.light-theme-toggle-button:hover {
  border-color:transparent; transform:translateY(-2px);
  background:linear-gradient(45deg,#ec4899,#8b5cf6); color:#fff;
  box-shadow:0 6px 16px rgba(236,72,153,0.3);
}
.light-action-icon { font-size:1.1rem; z-index:2; }
.light-user-avatar-small {
  width:38px; height:38px; border-radius:50%;
  background:linear-gradient(45deg,#ec4899,#8b5cf6);
  display:flex; align-items:center; justify-content:center;
  color:#fff; font-weight:700; font-size:0.95rem;
  box-shadow:0 4px 10px rgba(236,72,153,0.25); }

/* ─── Search / filter bar ────────────────────────────── */
.light-search-filter-container {
  display:flex; justify-content:space-between; align-items:center;
  margin-bottom:1.5rem; gap:1rem; background:#fff;
  padding:1rem 1.5rem; border-radius:16px;
  box-shadow:0 2px 12px rgba(0,0,0,0.05); border:1px solid #f1f5f9;
}
.light-search-container { position:relative; flex:1; max-width:380px; }
.search-icon { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:#94a3b8; font-size:15px; }
.light-search-input {
  width:100%; padding:0.65rem 1rem 0.65rem 2.75rem;
  border:1.5px solid #e2e8f0; border-radius:12px; background:#f8fafc;
  color:#1e293b; font-size:0.9rem; transition:all 0.25s;
}
.light-search-input:focus { outline:none; border-color:#ec4899; background:#fff; box-shadow:0 0 0 3px rgba(236,72,153,0.08); }
.light-search-input::placeholder { color:#cbd5e1; }
.view-sort-container { display:flex; gap:0.75rem; align-items:center; }
.view-toggle { display:flex; background:#f1f5f9; border-radius:10px; overflow:hidden; }
.view-btn { background:transparent; border:none; color:#94a3b8; padding:7px 11px; cursor:pointer; transition:all 0.2s; font-size:1rem; }
.view-btn.active { background:linear-gradient(45deg,#ec4899,#8b5cf6); color:#fff; }
.light-sort-select {
  padding:0.6rem 0.875rem; border:1.5px solid #e2e8f0; border-radius:10px;
  background:#f8fafc; color:#1e293b; font-size:0.875rem; cursor:pointer;
}
.light-sort-select:focus { outline:none; border-color:#ec4899; }

/* ─── Section header ─────────────────────────────────── */
.light-section-header {
  display:flex; justify-content:space-between; align-items:center;
  margin-bottom:1.5rem; background:#fff; padding:1rem 1.5rem;
  border-radius:16px; box-shadow:0 2px 12px rgba(0,0,0,0.05); border:1px solid #f1f5f9;
}
.light-section-header h2 { font-size:1.25rem; font-weight:700; color:#0f172a; margin:0; }
.light-section-action {
  display:flex; align-items:center; gap:0.5rem; padding:0.6rem 1.25rem;
  background:linear-gradient(45deg,#ec4899,#8b5cf6); color:#fff; border:none;
  border-radius:10px; cursor:pointer; transition:all 0.25s; font-weight:600;
  font-size:0.875rem; box-shadow:0 4px 12px rgba(236,72,153,0.25);
}
.light-section-action:hover { transform:translateY(-2px); box-shadow:0 8px 20px rgba(236,72,153,0.4); }

/* ─── Stats bar ──────────────────────────────────────── */
.op-stats-bar {
  display:flex; gap:1rem; margin-bottom:1.5rem; flex-wrap:wrap;
}
.op-stat {
  flex:1; min-width:100px; background:#fff; border:1px solid #f1f5f9;
  border-radius:14px; padding:1rem 1.25rem;
  box-shadow:0 2px 8px rgba(0,0,0,0.04);
  display:flex; flex-direction:column; align-items:center; gap:2px;
  transition:transform 0.2s;
}
.op-stat:hover { transform:translateY(-2px); }
.op-stat-num { font-size:1.75rem; font-weight:800; color:#0f172a; line-height:1; }
.op-stat-label { font-size:0.75rem; color:#94a3b8; font-weight:500; text-transform:uppercase; letter-spacing:0.05em; }
.op-stat-active .op-stat-num { color:#10b981; }
.op-stat-inactive .op-stat-num { color:#f59e0b; }
.op-stat-video .op-stat-num { color:#8b5cf6; }

/* ─── Form container ─────────────────────────────────── */
.op-form-container {
  background:#fff; border:1px solid #f1f5f9; border-radius:20px;
  padding:1.75rem; margin-bottom:2rem;
  box-shadow:0 4px 24px rgba(0,0,0,0.06);
  animation:slideDown 0.25s ease;
}
@keyframes slideDown { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:none} }
.op-form-header {
  display:flex; justify-content:space-between; align-items:center;
  margin-bottom:1.5rem; padding-bottom:1rem; border-bottom:1px solid #f1f5f9;
}
.op-form-header h3 { font-size:1.15rem; font-weight:700; color:#0f172a; margin:0; }
.op-form { display:flex; flex-direction:column; gap:1.25rem; }
.op-form-row { display:grid; grid-template-columns:1fr 1fr; gap:1.25rem; }
.op-form-group { display:flex; flex-direction:column; gap:0.4rem; }
.op-form-group-2 { grid-column:span 2 / span 2; }
.op-label { font-size:0.8rem; font-weight:600; color:#475569; text-transform:uppercase; letter-spacing:0.04em; }
.op-input {
  width:100%; padding:0.7rem 0.875rem; border:1.5px solid #e2e8f0;
  border-radius:10px; background:#f8fafc; color:#0f172a; font-size:0.9rem;
  transition:all 0.2s; font-family:inherit;
}
.op-input:focus { outline:none; border-color:#ec4899; background:#fff; box-shadow:0 0 0 3px rgba(236,72,153,0.08); }
.op-input::placeholder { color:#cbd5e1; }
.op-textarea { resize:vertical; min-height:80px; }

/* Upload zone */
.op-upload-zone {
  border:2px dashed #e2e8f0; border-radius:12px; overflow:hidden;
  cursor:pointer; transition:all 0.2s; min-height:100px;
  position:relative; background:#f8fafc;
}
.op-upload-zone:hover { border-color:#ec4899; background:#fdf2f8; }
.op-upload-placeholder {
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  gap:0.5rem; height:100%; min-height:100px; padding:1rem;
  color:#94a3b8; font-size:0.85rem; text-align:center;
}
.op-upload-icon { font-size:2rem; }
.op-upload-hidden {
  position:absolute; inset:0; opacity:0; cursor:pointer; width:100%; height:100%;
}
.op-preview-wrap { position:relative; width:100%; }
.op-preview-img { width:100%; height:120px; object-fit:cover; display:block; }
.op-preview-overlay {
  position:absolute; inset:0; background:rgba(0,0,0,0.4);
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  opacity:0; transition:opacity 0.2s; gap:0.5rem;
}
.op-preview-wrap:hover .op-preview-overlay { opacity:1; }
.op-preview-change { color:#fff; font-size:0.85rem; font-weight:600; }

.op-media-remove-btn {
  background:#ef4444; color:#fff; border:none; border-radius:50%;
  width:24px; height:24px; display:flex; align-items:center; justify-content:center;
  font-size:1.2rem; cursor:pointer; transition:all 0.2s;
  box-shadow:0 2px 8px rgba(239,68,68,0.3);
}
.op-media-remove-btn:hover { background:#dc2626; transform:scale(1.1); }
.op-media-remove-btn-video { position:absolute; top:5px; right:5px; }

.op-video-preview-wrap { display:flex; flex-direction:column; gap:0.5rem; padding:0.75rem; position:relative; }
.op-preview-video { width:100%; max-height:160px; display:block; border-radius:8px; background:#000; }
.op-video-preview-actions { display:flex; gap:0.5rem; align-items:center; }
.op-video-change-btn {
  display:inline-flex; align-items:center; justify-content:center; gap:0.4rem;
  padding:0.4rem 0.875rem; background:#f1f5f9; border:1.5px solid #cbd5e1;
  border-radius:8px; font-size:0.8rem; font-weight:600; color:#475569;
  cursor:pointer; transition:all 0.2s;
}
.op-video-change-btn:hover { border-color:#ec4899; color:#ec4899; background:rgba(236,72,153,0.05); }

/* Toggle switch */
.op-toggle-label { display:flex; align-items:center; gap:0.75rem; cursor:pointer; user-select:none; font-size:0.9rem; color:#475569; font-weight:500; }
.op-toggle { width:44px; height:24px; border-radius:12px; background:#e2e8f0; position:relative; transition:background 0.25s; flex-shrink:0; cursor:pointer; }
.op-toggle.op-toggle-on { background:linear-gradient(45deg,#ec4899,#8b5cf6); }
.op-toggle-knob { position:absolute; top:3px; left:3px; width:18px; height:18px; border-radius:50%; background:#fff; transition:transform 0.25s; box-shadow:0 1px 4px rgba(0,0,0,0.2); }
.op-toggle-on .op-toggle-knob { transform:translateX(20px); }

.op-form-footer { display:flex; justify-content:space-between; align-items:center; padding-top:0.5rem; border-top:1px solid #f1f5f9; margin-top:0.25rem; }
.op-form-actions { display:flex; gap:0.75rem; }
.op-btn-primary {
  padding:0.65rem 1.5rem; background:linear-gradient(45deg,#ec4899,#8b5cf6);
  color:#fff; border:none; border-radius:10px; cursor:pointer;
  font-weight:600; font-size:0.9rem; transition:all 0.25s;
  box-shadow:0 4px 12px rgba(236,72,153,0.25);
}
.op-btn-primary:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 20px rgba(236,72,153,0.4); }
.op-btn-primary:disabled { opacity:0.6; cursor:not-allowed; }
.op-btn-cancel {
  padding:0.65rem 1.25rem; background:#f1f5f9; color:#64748b;
  border:1.5px solid #e2e8f0; border-radius:10px; cursor:pointer;
  font-weight:500; font-size:0.9rem; transition:all 0.2s;
}
.op-btn-cancel:hover { background:#e2e8f0; color:#1e293b; }

/* ─── Empty state ────────────────────────────────────── */
.op-empty-state {
  text-align:center; padding:4rem 2rem; background:#fff;
  border:1px solid #f1f5f9; border-radius:20px;
  box-shadow:0 4px 20px rgba(0,0,0,0.04); max-width:480px; margin:0 auto;
}
.op-empty-icon { font-size:3.5rem; margin-bottom:1rem; opacity:0.7; }
.op-empty-state h3 { color:#0f172a; font-size:1.25rem; font-weight:700; margin-bottom:0.5rem; }
.op-empty-state p { color:#64748b; margin-bottom:1.75rem; font-size:0.9rem; }

/* ─── Cards grid ─────────────────────────────────────── */
.op-cards-grid {
  display:grid; grid-template-columns:repeat(auto-fill,minmax(300px,1fr));
  gap:1.25rem; margin-top:0.5rem;
}
.op-list-view { grid-template-columns:1fr; }

/* ─── Card ───────────────────────────────────────────── */
.op-card {
  background:#fff; border:1px solid #f1f5f9; border-radius:18px;
  overflow:hidden; box-shadow:0 2px 12px rgba(0,0,0,0.05);
  transition:all 0.3s; display:flex; flex-direction:column;
}
.op-card:hover { transform:translateY(-5px); box-shadow:0 12px 32px rgba(236,72,153,0.12); border-color:#fce7f3; }
.op-card-inactive { opacity:0.72; }

/* list-view horizontal layout */
.op-card-list { flex-direction:row; }
.op-card-list .op-card-media { width:220px; flex-shrink:0; border-radius:0; }
.op-card-list .op-card-body { flex:1; }

/* Media */
.op-card-media {
  position:relative; height:175px; background:linear-gradient(135deg,#fdf2f8,#e0e7ff);
  overflow:hidden; flex-shrink:0;
}
.op-card-media-el { width:100%; height:100%; object-fit:cover; display:block; transition:transform 0.4s ease; }
.op-card:hover .op-card-media-el { transform:scale(1.04); }
.op-card-media-placeholder { display:flex; align-items:center; justify-content:center; height:100%; font-size:3.5rem; opacity:0.35; }

/* Video Overlay */
.op-video-overlay-icon {
  position:absolute; inset:0; background:rgba(0,0,0,0.25);
  display:flex; align-items:center; justify-content:center;
  transition:background 0.3s; cursor:pointer;
}
.op-card:hover .op-video-overlay-icon { background:rgba(0,0,0,0.4); }
.op-play-button {
  width:54px; height:54px; background:rgba(255,255,255,0.95);
  border-radius:50%; display:flex; align-items:center; justify-content:center;
  color:#ec4899; font-size:1.5rem; padding-left:4px;
  box-shadow:0 4px 15px rgba(0,0,0,0.2); transition:all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.op-card:hover .op-play-button { transform:scale(1.15); background:#fff; box-shadow:0 8px 25px rgba(236,72,153,0.4); }

/* Lightbox */
.op-video-lightbox {
  position:fixed; inset:0; background:rgba(15,23,42,0.95);
  display:flex; align-items:center; justify-content:center; z-index:9999;
  backdrop-filter:blur(8px); animation:fadeIn 0.3s ease;
}
@keyframes fadeIn { from{opacity:0} to{opacity:1} }
.op-video-lightbox-content {
  position:relative; width:90%; max-width:1000px; aspect-ratio:16/9;
  background:#000; border-radius:20px; overflow:hidden;
  box-shadow:0 25px 50px -12px rgba(0,0,0,0.5);
}
.op-lightbox-video { width:100%; height:100%; }
.op-video-close {
  position:absolute; top:20px; right:20px; background:rgba(255,255,255,0.2);
  border:none; color:#fff; font-size:2rem; width:44px; height:44px;
  border-radius:50%; cursor:pointer; display:flex; align-items:center; justify-content:center;
  transition:all 0.2s; z-index:10;
}
.op-video-close:hover { background:rgba(255,255,255,0.3); transform:rotate(90deg); }

/* Badges */
.op-discount-badge {
  position:absolute; top:10px; right:10px;
  background:linear-gradient(45deg,#ec4899,#8b5cf6);
  color:#fff; font-weight:800; font-size:0.9rem;
  padding:4px 10px; border-radius:999px;
  box-shadow:0 4px 12px rgba(236,72,153,0.35);
  letter-spacing:-0.01em;
}
.op-video-pill {
  position:absolute; bottom:10px; left:10px;
  background:rgba(0,0,0,0.65); backdrop-filter:blur(4px);
  color:#fff; font-size:0.72rem; font-weight:600;
  padding:3px 9px; border-radius:999px;
}
.op-status-pill {
  position:absolute; top:10px; left:10px;
  font-size:0.72rem; font-weight:700; padding:3px 9px; border-radius:999px;
}
.op-pill-active { background:rgba(16,185,129,0.15); color:#059669; border:1px solid rgba(16,185,129,0.3); }
.op-pill-inactive { background:rgba(245,158,11,0.15); color:#d97706; border:1px solid rgba(245,158,11,0.3); }

/* Body */
.op-card-body { padding:1.125rem; display:flex; flex-direction:column; gap:0.5rem; flex:1; }
.op-card-title { font-size:1rem; font-weight:700; color:#0f172a; margin:0; line-height:1.3;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.op-card-desc { font-size:0.82rem; color:#64748b; margin:0; line-height:1.5;
  display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; }
.op-card-meta { margin-top:auto; }
.op-meta-row { display:flex; align-items:center; gap:0.4rem; font-size:0.78rem; color:#94a3b8; }
.op-meta-icon { font-size:0.85rem; }
.op-card-video {
  display:block; width:100%; max-height:200px; object-fit:cover;
  background:#000; border-top:1px solid #f1f5f9;
}
.op-card-actions { display:flex; gap:0.5rem; margin-top:0.75rem; padding-top:0.75rem; border-top:1px solid #f1f5f9; }
.op-btn-edit {
  flex:1; padding:0.55rem 0.875rem; background:linear-gradient(45deg,#ec4899,#8b5cf6);
  color:#fff; border:none; border-radius:8px; cursor:pointer;
  font-weight:600; font-size:0.82rem; transition:all 0.2s;
}
.op-btn-edit:hover { transform:translateY(-1px); box-shadow:0 6px 14px rgba(236,72,153,0.3); }
.op-btn-delete {
  padding:0.55rem 0.875rem; background:#fef2f2; color:#ef4444;
  border:1.5px solid #fee2e2; border-radius:8px; cursor:pointer;
  font-size:0.9rem; transition:all 0.2s;
}
.op-btn-delete:hover { background:#ef4444; color:#fff; border-color:#ef4444; }

/* ─── Responsive ─────────────────────────────────────── */
@media (max-width:1024px) {
  .light-main-content { padding:1.5rem; }
}
@media (max-width:768px) {
  .light-mobile-header { display:flex; }
  .light-sidebar { transform:translateX(-100%); }
  .light-sidebar.open { transform:translateX(0); }
  .light-close-sidebar { display:block; }
  .light-main-content { margin-left:0; padding:1rem; }
  .light-welcome-header { flex-direction:column; align-items:flex-start; gap:0.75rem; }
  .light-welcome-header h1 { font-size:1.75rem; }
  .light-search-filter-container { flex-direction:column; align-items:stretch; }
  .light-section-header { flex-direction:column; align-items:stretch; gap:0.75rem; }
  .op-cards-grid { grid-template-columns:1fr; }
  .op-form-row { grid-template-columns:1fr; }
  .op-form-group-2 { grid-column:span 1; }
  .op-card-list { flex-direction:column; }
  .op-card-list .op-card-media { width:100%; height:160px; }
  .op-form-footer { flex-direction:column; gap:1rem; align-items:stretch; }
  .op-form-actions { justify-content:flex-end; }
  .op-stats-bar { gap:0.75rem; }
  .op-stat { min-width:calc(50% - 0.375rem); }
}

/* Focus styles */
.light-action-button:focus, .light-theme-toggle-button:focus,
.op-btn-primary:focus, .light-section-action:focus,
.op-btn-edit:focus, .op-btn-delete:focus {
  outline:2px solid #ec4899; outline-offset:2px;
}
      `}</style>
    </div>
  )
}

export default OffresPage
